import { supabase } from '../config/supabase';
import { clubsService } from './clubs';
import { User } from '../types';

/**
 * User Coordinator Service
 * Cross-domain coordinator for user-related operations
 * Orchestrates auth + clubs + profile operations
 */
export const userService = {
  /**
   * Complete onboarding process
   * Joins selected clubs and marks onboarding as complete
   */
  async completeOnboarding(clubIds?: string[]): Promise<void> {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) throw new Error('Not authenticated');

    try {
      // Join clubs if provided
      if (clubIds && clubIds.length > 0) {
        await clubsService.joinClubs(clubIds);
      }

      // Mark onboarding as complete
      const { error } = await supabase
        .from('profiles')
        .update({
          onboarding_completed: true,
          onboarding_completed_at: new Date().toISOString(),
        })
        .eq('id', session.user.id);

      if (error) throw error;
    } catch (error) {
      // If anything fails, throw error so UI can handle it
      // Don't partially complete onboarding
      throw error;
    }
  },

  /**
   * Get current user with their club memberships
   */
  async getCurrentUserWithClubs(): Promise<User & { clubs?: any[] }> {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) throw new Error('Not authenticated');

    // Get user profile
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', session.user.id)
      .single();

    if (profileError) throw profileError;
    if (!profile) throw new Error('Profile not found');

    // Get user's clubs
    try {
      const userClubs = await clubsService.getUserClubs(session.user.id);
      return {
        ...profile,
        clubs: userClubs,
      };
    } catch (error) {
      // If clubs query fails, still return profile without clubs
      console.error('Failed to load user clubs:', error);
      return profile;
    }
  },
};
